import { useTranslation } from 'react-i18next';

import { useDeletedDocuments, usePurgeDocument, useRestoreDocument } from '@/entities/document';
import { FormError } from '@/shared/forms';
import { formatDateTime } from '@/shared/lib/datetime';
import { Button, ConfirmDialog, LoadingState } from '@/shared/ui';
import { useState } from 'react';

/** The notes this person may restore, inside the workspace trash dialog. */
export function DeletedDocuments({
  workspaceId,
  timeZone,
}: {
  workspaceId: string;
  timeZone?: string | undefined;
}) {
  const { t, i18n } = useTranslation();
  const deleted = useDeletedDocuments(workspaceId);
  const restore = useRestoreDocument();
  const purge = usePurgeDocument();
  const [purging, setPurging] = useState<{ id: string; name: string } | null>(null);
  if (deleted.isPending) return <LoadingState />;
  if (!deleted.data || deleted.data.length === 0) return null;
  return (
    <section aria-label={t('documents.trashTitle')} data-testid="deleted-documents">
      <h3>{t('documents.trashTitle')}</h3>
      <FormError error={restore.error ?? purge.error} />
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {deleted.data.map((document) => (
          <li
            key={document.id}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 'var(--space-2)',
              alignItems: 'center',
              padding: 'var(--space-2) 0',
            }}
          >
            <span style={{ flex: '1 1 12rem', minWidth: 0, overflowWrap: 'anywhere' }}>
              <strong>{document.name}</strong>
              <br />
              <small>
                {t('trash.deletedAt', {
                  when: formatDateTime(document.trashed_at, i18n.language, timeZone),
                })}
              </small>
            </span>
            <Button
              variant="secondary"
              disabled={restore.isPending}
              onClick={() => {
                restore.mutate(document.id);
              }}
            >
              {t('trash.restore')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setPurging(document);
              }}
            >
              {t('trash.purge')}
            </Button>
          </li>
        ))}
      </ul>
      {purging && (
        <ConfirmDialog
          title={t('documents.purgeTitle')}
          message={t('documents.purgeMessage', { name: purging.name })}
          confirmLabel={t('trash.purge')}
          pending={purge.isPending}
          onCancel={() => {
            setPurging(null);
          }}
          onConfirm={() => {
            purge.mutate(purging.id, {
              onSettled: () => {
                setPurging(null);
              },
            });
          }}
        />
      )}
    </section>
  );
}
